import { useEffect, useMemo, useState } from 'react';
import { useTable } from 'react-table';
import PropTypes from 'prop-types';

import { CopyToClipboard, formatData, Tabs } from '@utilities';
import { getCopyTableValue } from '@utilities/helpers';
import SplineChart from '@utilities/charts/SplineChart';

import { DISPLAY_AS } from '../../utilities/helpers';
import CollapsibleTable from '../CollapsibleTable';

import styles from './_index.module.scss';

const VanWestendorpSegment = ({ displayAs, segment }) => {
  const { getTableProps, headerGroups, prepareRow, rows } = useTable({
    columns: segment?.table?.columns ?? [],
    data: segment?.table?.data ?? [],
  });

  const isDisplayAsPercent = displayAs === DISPLAY_AS.PERCENT.value;
  const chart = isDisplayAsPercent ? segment?.chartPct : segment?.chartCount;
  const chartMax = isDisplayAsPercent
    ? 1
    : chart?.series?.reduce(
        (max, series) => Math.max(max, ...(series.data ?? []).map(({ y }) => y)),
        0
      );

  return (
    <>
      {chart && (
        <SplineChart
          chartTitle={`Responses by ${
            isDisplayAsPercent ? DISPLAY_AS.PERCENT.label : DISPLAY_AS.COUNT.label
          }`}
          exportName={chart.title}
          series={chart.series}
          tooltip={{
            pointFormatter: function () {
              return `<span style="color:${this.color}">●</span> ${
                this.series.name
              }: <b>${formatData({ format: chart.format, value: this.y })}</b><br/>`;
            },
          }}
          xAxis={chart.xAxis}
          yAxis={{
            labels: {
              formatter: function () {
                return formatData({ format: chart.format, value: this.value });
              },
            },
            max: chartMax,
            min: 0,
            title: null,
          }}
        />
      )}

      {segment?.table && (
        <>
          <div className={styles['van-westendorp-table-actions']}>
            <CopyToClipboard
              text="Copy Table"
              value={getCopyTableValue({ columns: segment.table.columns, data: rows })}
            />
          </div>
          <CollapsibleTable rowCount={rows.length}>
            <div className={styles['van-westendorp-table-container']}>
              <table className={styles['van-westendorp-table']} {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup, index) => (
                    <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                      {headerGroup.headers.map((column) => (
                        <th {...column.getHeaderProps()} key={column.id}>
                          {column.render('Header')}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} key={rowIndex}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()} key={cell.column.id}>
                            {cell.render('Cell')}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CollapsibleTable>
        </>
      )}
    </>
  );
};

VanWestendorpSegment.propTypes = {
  displayAs: PropTypes.string,
  segment: PropTypes.shape({
    chartCount: PropTypes.object,
    chartPct: PropTypes.object,
    table: PropTypes.object,
  }),
};

const VanWestendorp = ({ data, displayAs = DISPLAY_AS.PERCENT.value }) => {
  const segments = data?.segments ?? [];
  const tabs = useMemo(
    () => segments.map(({ id, name }) => ({ label: name ?? id, value: id })),
    [segments]
  );
  const [activeTab, setActiveTab] = useState(tabs[0]?.value);

  useEffect(() => {
    setActiveTab((currentTab) =>
      tabs.some(({ value }) => value === currentTab) ? currentTab : tabs[0]?.value
    );
  }, [tabs]);

  const activeSegment = segments.find(({ id }) => id === activeTab) ?? segments[0];

  return (
    <div className={styles['van-westendorp']}>
      {tabs.length > 1 && (
        <Tabs
          aria-label="Compare by group"
          onChange={setActiveTab}
          options={tabs}
          value={activeTab}
        />
      )}
      <VanWestendorpSegment displayAs={displayAs} segment={activeSegment} />
    </div>
  );
};

VanWestendorp.propTypes = {
  data: PropTypes.shape({
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        chartCount: PropTypes.object,
        chartPct: PropTypes.object,
        id: PropTypes.string,
        name: PropTypes.string,
        table: PropTypes.object,
      })
    ),
  }),
  displayAs: PropTypes.string,
};

export default VanWestendorp;
